/**
 * The search query language.
 *
 * Plain words are the common case and stay the common case: every term has to
 * appear somewhere. Everything beyond that is opt-in punctuation, so a query
 * that contains none of it behaves exactly as it always did.
 *
 *   deploy notes        both words, anywhere
 *   "release notes"     that exact phrase
 *   -draft              excluding anything containing `draft`
 *   tag:project         tagged `project`, or any tag nested under it
 *   path:Work           in the `Work` folder or below it
 *   title:spec          matching in the title only
 *
 * Parsing is deliberately total: an unterminated quote or a bare `tag:` is a
 * half-typed query, not an error, and must still return something sensible
 * while the user is typing.
 */

/** A term that must (or must not) appear. */
export interface QueryTerm {
  /** Lowercased text to look for. */
  value: string;
  /** True when the text was quoted and must match contiguously. */
  phrase: boolean;
  /** Restrict the match to the document title. */
  titleOnly: boolean;
}

export interface ParsedQuery {
  /** Terms that must match. */
  include: QueryTerm[];
  /** Terms that must not match. */
  exclude: QueryTerm[];
  /** `tag:` filters; a document matches a filter by tag or by nesting under it. */
  tags: string[];
  /** `path:` filters; matched against the folder path prefix. */
  paths: string[];
  /** True when the query asks for nothing at all. */
  empty: boolean;
}

const EMPTY: ParsedQuery = { include: [], exclude: [], tags: [], paths: [], empty: true };

/** Split a query into fields, quoted phrases and bare words. */
function lex(
  query: string,
): { negated: boolean; field: string | null; value: string; quoted: boolean }[] {
  const out: { negated: boolean; field: string | null; value: string; quoted: boolean }[] = [];
  let i = 0;
  while (i < query.length) {
    while (i < query.length && /\s/.test(query[i]!)) i++;
    if (i >= query.length) break;

    let negated = false;
    if (query[i] === '-' && i + 1 < query.length && !/\s/.test(query[i + 1]!)) {
      negated = true;
      i++;
    }

    let field: string | null = null;
    const fieldMatch = /^([a-z]+):/i.exec(query.slice(i));
    if (fieldMatch) {
      field = (fieldMatch[1] ?? '').toLowerCase();
      i += fieldMatch[0].length;
    }

    let value: string;
    let quoted = false;
    if (query[i] === '"') {
      quoted = true;
      i++;
      const end = query.indexOf('"', i);
      // An unterminated quote takes the rest of the query: the user is mid-typing.
      value = end === -1 ? query.slice(i) : query.slice(i, end);
      i = end === -1 ? query.length : end + 1;
    } else {
      const start = i;
      while (i < query.length && !/\s/.test(query[i]!)) i++;
      value = query.slice(start, i);
    }

    if (value.trim() !== '') out.push({ negated, field, value: value.trim(), quoted });
  }
  return out;
}

/** Parse a raw query string. Never throws; a malformed query just matches less. */
export function parseQuery(query: string): ParsedQuery {
  const tokens = lex(query);
  if (tokens.length === 0) return EMPTY;

  const parsed: ParsedQuery = { include: [], exclude: [], tags: [], paths: [], empty: false };
  for (const tok of tokens) {
    const value = tok.value.toLowerCase();
    if (tok.field === 'tag') {
      // `-tag:x` is not a filter but an exclusion, and reads better as a term.
      if (tok.negated)
        parsed.exclude.push({ value: `tag:${value}`, phrase: false, titleOnly: false });
      else parsed.tags.push(value.replace(/^#/, ''));
      continue;
    }
    if (tok.field === 'path') {
      parsed.paths.push(value);
      continue;
    }
    const term: QueryTerm = {
      value,
      phrase: tok.quoted,
      titleOnly: tok.field === 'title',
    };
    (tok.negated ? parsed.exclude : parsed.include).push(term);
  }

  parsed.empty =
    parsed.include.length === 0 &&
    parsed.exclude.length === 0 &&
    parsed.tags.length === 0 &&
    parsed.paths.length === 0;
  return parsed;
}

/** A tag filter matches the tag itself and anything nested under it. */
export function tagMatches(filter: string, tag: string): boolean {
  const t = tag.toLowerCase();
  return t === filter || t.startsWith(`${filter}/`);
}

/** A path filter matches the folder itself and anything below it. */
export function pathMatches(filter: string, folder: string | null | undefined): boolean {
  if (!folder) return false;
  const f = folder.toLowerCase();
  return f === filter || f.startsWith(`${filter}/`);
}

/** The fields a document exposes to a query. */
export interface Searchable {
  title: string;
  /** Body text with frontmatter already removed. */
  body: string;
  tags: string[];
  folder?: string | null;
}

function termMatches(term: QueryTerm, doc: Searchable): boolean {
  const title = doc.title.toLowerCase();
  if (term.value.startsWith('tag:')) {
    const wanted = term.value.slice(4);
    return doc.tags.some((t) => tagMatches(wanted, t));
  }
  if (title.includes(term.value)) return true;
  if (term.titleOnly) return false;
  return doc.body.toLowerCase().includes(term.value);
}

/** True when a document satisfies every part of the query. */
export function matchesQuery(doc: Searchable, q: ParsedQuery): boolean {
  if (q.empty) return true;
  if (q.tags.some((f) => !doc.tags.some((t) => tagMatches(f, t)))) return false;
  if (q.paths.length && !q.paths.some((f) => pathMatches(f, doc.folder))) return false;
  if (!q.include.every((t) => termMatches(t, doc))) return false;
  if (q.exclude.some((t) => termMatches(t, doc))) return false;
  return true;
}

/**
 * Rank a matching document. Higher is better.
 *
 * Title matches outrank body matches because a note whose *name* you typed is
 * almost always the one you meant; tag matches sit between the two.
 */
export function scoreMatch(doc: Searchable, q: ParsedQuery): number {
  if (q.include.length === 0) return 0;
  const title = doc.title.toLowerCase();
  let score = 0;
  for (const term of q.include) {
    if (title === term.value) score += 8;
    else if (title.startsWith(term.value)) score += 5;
    else if (title.includes(term.value)) score += 3;
    else if (doc.tags.some((t) => t.toLowerCase().includes(term.value))) score += 2;
    else score += 1;
  }
  return score;
}

/** The bare words in a query, for highlighting matches in the UI. */
export function highlightTerms(q: ParsedQuery): string[] {
  return q.include.filter((t) => !t.value.startsWith('tag:')).map((t) => t.value);
}
